import { DiscountType } from 'interfaces/order';
import { getBundleTotals } from 'modules/cart/components/cart-items/cart-bundle/utils';
import { hasBundles } from 'utils/dynamic-bundle';
import { GetPriceSummaryDataArgs, PriceSummaryDiscountItem } from '../types';
import { getAverageSaleSavingsPercentage } from './getAverageSaleSavingsPercentage';
import { getOriginItemsDiscountAmount } from './getOriginItemsDiscountAmount';
import { getSaleSavingsAmount } from './getSaleSavingsAmount';
import { getSubscriptionDiscountPercentage } from './getSubscriptionDiscountPercentage';

/**
 * Builds the ordered list of savings rows shown in the price summary.
 *
 * Each row is added only when that discount source has a positive amount so the
 * UI can render a compact breakdown without empty entries.
 */
export const getSavingsItems = ({
  activePlan,
  effectiveCart,
}: GetPriceSummaryDataArgs): PriceSummaryDiscountItem[] => {
  const saleSavings = getSaleSavingsAmount(effectiveCart);
  const subscriptionSavings = getOriginItemsDiscountAmount(effectiveCart, 'subscription');
  const savingsPlanSavings = getOriginItemsDiscountAmount(effectiveCart, 'savings_plan');
  const couponPercentageDiscount =
    effectiveCart.discount?.type === DiscountType.COUPON
      ? (effectiveCart.discount.definition?.percentage ?? 0) * 100
      : null;

  const savingsItems: PriceSummaryDiscountItem[] = [];

  if (saleSavings > 0) {
    savingsItems.push({
      amount: saleSavings,
      percentage: getAverageSaleSavingsPercentage(effectiveCart),
      type: 'offer',
    });
  }

  if (subscriptionSavings > 0) {
    savingsItems.push({
      amount: subscriptionSavings,
      percentage: getSubscriptionDiscountPercentage(effectiveCart),
      type: 'subscription',
    });
  }

  if (effectiveCart.discount?.type === DiscountType.REFERAL) {
    savingsItems.push({
      amount: effectiveCart.discount.totalDiscount,
      type: 'referal',
    });
  }

  if (
    effectiveCart.discount?.type === DiscountType.COUPON &&
    effectiveCart.discount.totalDiscount > 0
  ) {
    savingsItems.push({
      amount: effectiveCart.discount.totalDiscount,
      couponCode: effectiveCart.discount.code,
      percentage: couponPercentageDiscount ?? undefined,
      type: 'coupon',
    });
  }

  if (savingsPlanSavings > 0) {
    savingsItems.push({
      amount: savingsPlanSavings,
      percentage: activePlan?.discountPct,
      planName: activePlan?.planName,
      type: 'savings_plan',
    });
  }

  if (hasBundles(effectiveCart.items) && effectiveCart.discountOrigins) {
    // gets only bundle discounts, can have multiple bundle discounts, ex: bundle 15%, bundle 30% e.t.c
    const bundlesDiscounts = effectiveCart.discountOrigins.filter(
      (item) => item.type === 'dynamic_bundle'
    );

    // aggregates total discount in cents per bundle discount, ex: you may have 3 bundles of 15% and 2 bundles of 30%,
    // it will calculate total discount in cents for 15% and for 30%
    const bundlesPerDiscount = bundlesDiscounts.reduce<Map<number, number>>(
      (acc, discountOrigin) => {
        const bundleItems = effectiveCart.items.filter((item) =>
          discountOrigin.items?.includes(item.variantId)
        );
        const { totalDiscounts } = getBundleTotals({
          discountOrigins: effectiveCart.discountOrigins,
          items: bundleItems,
        });

        const discountInCentsForBundleType = totalDiscounts.find(
          (item) => item.type === 'dynamic_bundle'
        )?.amount;

        if (discountInCentsForBundleType === undefined) return acc;

        const currentDiscountInCents = acc.get(discountOrigin.value) ?? 0;
        acc.set(discountOrigin.value, currentDiscountInCents + discountInCentsForBundleType);
        return acc;
      },
      new Map()
    );

    if (bundlesPerDiscount.size > 0) {
      bundlesPerDiscount.forEach((cents, percent) => {
        savingsItems.push({
          amount: cents,
          percentage: percent,
          type: 'dynamic_bundle',
        });
      });
    }
  }

  return savingsItems;
};
